import React, { useState, useRef } from 'react';
import { Box, Typography, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Collapse, Divider as MuiDivider, Grid, Chip, Modal, Snackbar } from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { IPlayerState } from '../../../Core/State/IPlayerState';
import CardImage from './Card';
import MiniPokerGameDisplay from './MiniPokerGameDisplay';
import Tooltip from '@mui/material/Tooltip';
import AnalysisContent from '../../GameRoom/SubComponents/AnalysisContent';
import { t } from '../../../i18n';

const fadeInOut = keyframes`
  0% { opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { opacity: 0; }
`;

const WinnerDisplay = styled(Box)<{ isPinned: boolean }>(({ theme, isPinned }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    borderRadius: '15px',
    padding: '20px',
    boxShadow: '0 0 20px rgba(255, 215, 0, 0.5)',
    color: 'white',
    textAlign: 'center',
    zIndex: 1000,
    animation: isPinned ? 'none' : `${fadeInOut} 5s ease-in-out`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '90%',
    height: '90%',
    overflow: 'hidden',
}));

const ScrollableContent = styled(Box)({
    overflowY: 'auto',
    overflowX: 'hidden',
    width: '100%',
    height: '100%',
    padding: '10px',
    '&::-webkit-scrollbar': {
        width: '8px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'rgba(255, 255, 255, 0.1)',
    },
    '&::-webkit-scrollbar-thumb': {
        background: 'rgba(255, 255, 255, 0.3)',
        borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
        background: 'rgba(255, 255, 255, 0.5)',
    },
});

const SectionTitle = styled(Typography)({
    fontSize: '16px',
    fontWeight: 'bold',
    marginTop: '8px',
    marginBottom: '4px',
    color: '#FFD700',
});

const GameplayRow = styled(Box)({
    display: 'flex',
    width: '100%',
    gap: '10px',
    marginTop: '20px',
    flexGrow: 1,
    maxHeight: 'calc(60% + 100px)',
});

const GameplayColumn = styled(Box)({
    flex: '1 1 calc(50% - 5px)',
    maxWidth: 'calc(50% - 5px)',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '0',
    overflow: 'hidden',
    height: '100%',
});

const GameReplayWrapper = styled(Box)({
    flexGrow: 1,
    overflow: 'auto',
    height: 'calc(100% - 30px)',
});

const GameplaySummaryContainer = styled(Box)({
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    height: 'calc(100% - 30px)',
    maxHeight: '750px',
});

const ActionChip = styled(Box)({
    display: 'inline-block',
    padding: '3px 6px',
    margin: '0 3px',
    borderRadius: '4px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: '#006400',
    fontSize: '12px',
    fontWeight: 'bold',
});

const VerticalDivider = styled(Box)({
    width: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    margin: '0 5px',
});

const WinnersTable = styled(TableContainer)(({ theme }) => ({
    marginBottom: '20px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    '& .MuiTableCell-root': {
        padding: '12px',
        fontSize: '1rem',
        color: 'white',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        textAlign: 'center',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        fontWeight: 'bold',
        fontSize: '1.1rem',
    },
}));

const WinnersTitle = styled(Typography)({
    fontSize: '20px',
    fontWeight: 'bold',
    marginTop: '12px',
    marginBottom: '12px',
    color: '#FFD700',
    textAlign: 'center',
});

const RoomIdDisplay = styled(Box)({
    position: 'absolute',
    top: '10px',
    left: '10px',
    fontSize: '14px',
    color: '#FFD700',
    fontWeight: 'bold',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
});

const AnalysisModal = styled(Modal)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

interface WinnersDisplayProps {
    winners: any[];
    nonWinners: any[] | null;
    communityCards: string[];
    isPinned: boolean;
    onPinToggle: () => void;
    formatCurrency: (amount: number) => string;
    players: IPlayerState[];
    playerTurnHistory: {
        time: string;
        username: string;
        userId: string;
        roundName: string;
        availableActions: Array<any>;
        pot: number;
        communityCards: string[];
        playerCurrentBets: {
            userId: string;
            amount: number;
            username: string;
        }[];
        playerBalances: {
            userId: string;
            username: string;
            amount: number;
        }[];
        playerCards: {
            userId: string;
            username: string;
            cards: string[];
        }[];
        smallBlindId: string;
        bigBlindId: string;
        smallBlindUsername: string;
        bigBlindUsername: string;
        dealerId: string;
        dealerUsername: string;
        selectedAction: {
            action: string;
            amount: number;
        };
    }[];
    roomId: string;
    gameId: string | null;
}

const WinnersDisplay: React.FC<WinnersDisplayProps> = ({
    winners,
    nonWinners,
    communityCards,
    isPinned,
    onPinToggle,
    formatCurrency,
    players,
    playerTurnHistory,
    roomId,
    gameId
}) => {
    // Add validation check at the start
    if (!playerTurnHistory?.length || !winners?.length) {
        return null;
    }

    const [expandedRows, setExpandedRows] = useState<{ [key: number]: boolean }>({});
    const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [copyStatus, setCopyStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const toggleRow = (index: number) => {
        setExpandedRows(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const renderAvailableActions = (actions: Array<{ type: string; minAmount: number; maxAmount: number }>) => {
        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', padding: '6px 0' }}>
                {actions.map((action, index) => (
                    <ActionChip key={index}>
                        {action.type.toUpperCase()}
                        {action.minAmount > 0 && ` (${formatCurrency(action.minAmount)}-${formatCurrency(action.maxAmount)})`}
                    </ActionChip>
                ))}
            </Box>
        );
    };

    const renderExtraData = (turn: any) => {
        return (
            <Box sx={{ margin: 1, backgroundColor: 'rgba(0, 0, 0, 0.9)', padding: 2, borderRadius: 2, color: 'white' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1 }}>{t('GameRoom:winnersDisplay.availableActions')}</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {Array.isArray(turn.availableActions) ? (
                                turn.availableActions.map((action: any, index: number) => (
                                    <Chip
                                        key={index}
                                        label={`${action.type} ${action.minAmount > 0 ? `($${action.minAmount}-$${action.maxAmount})` : ''}`}
                                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', color: 'white' }}
                                    />
                                ))
                            ) : (
                                <Typography variant="body2" sx={{ color: 'white' }}>{t('GameRoom:winnersDisplay.noAvailableActions')}</Typography>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1 }}>{t('GameRoom:winnersDisplay.communityCards')}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            {Array.isArray(turn.communityCards) && turn.communityCards.length > 0 ? (
                                turn.communityCards.map((card: string, index: number) => (
                                    <Tooltip key={index} title={card} arrow>
                                        <Box>
                                            <CardImage card={card} size={60} />
                                        </Box>
                                    </Tooltip>
                                ))
                            ) : (
                                <Typography variant="body2" sx={{ color: 'white' }}>{t('GameRoom:winnersDisplay.noCommunityCardsYet')}</Typography>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <MuiDivider sx={{ my: 2, backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
                        <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1 }}>{t('GameRoom:winnersDisplay.playerInformation')}</Typography>
                        <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t('GameRoom:winnersDisplay.player')}</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t('GameRoom:winnersDisplay.currentBet')}</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t('GameRoom:winnersDisplay.balance')}</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t('GameRoom:winnersDisplay.cards')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {turn.playerBalances.map((player: any, index: number) => {
                                        const currentBet = turn.playerCurrentBets.find((bet: any) => bet.userId === player.userId)?.amount || 0;
                                        const cards = turn.playerCards.find((cards: any) => cards.userId === player.userId)?.cards || [];
                                        return (
                                            <TableRow key={index}>
                                                <TableCell sx={{ color: 'white' }}>
                                                    {player.username}
                                                    {player.userId === turn.dealerId && ' (D)'}
                                                    {player.userId === turn.smallBlindId && ' (SB)'}
                                                    {player.userId === turn.bigBlindId && ' (BB)'}
                                                </TableCell>
                                                <TableCell sx={{ color: 'white' }}>${currentBet}</TableCell>
                                                <TableCell sx={{ color: 'white' }}>${player.amount}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                        {cards.map((card: string, cardIndex: number) => (
                                                            <Tooltip key={cardIndex} title={card} arrow>
                                                                <Box>
                                                                    <CardImage card={card} size={50} /> {/* Increased size */}
                                                                </Box>
                                                            </Tooltip>
                                                        ))}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            </Box>
        );
    };

    const renderGameplaySummary = () => {
        return (
            <TableContainer component={Paper} style={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ width: '40px', backgroundColor: 'white', color: 'black' }}></TableCell>
                            <TableCell style={{ width: '40px', backgroundColor: 'white', color: 'black' }}>#</TableCell>
                            <TableCell style={{ backgroundColor: 'white', color: 'black' }}>{t('GameRoom:winnersDisplay.time')}</TableCell>
                            <TableCell style={{ backgroundColor: 'white', color: 'black' }}>{t('GameRoom:winnersDisplay.player')}</TableCell>
                            <TableCell style={{ backgroundColor: 'white', color: 'black' }}>{t('GameRoom:winnersDisplay.round')}</TableCell>
                            <TableCell style={{ backgroundColor: 'white', color: 'black' }}>{t('GameRoom:winnersDisplay.action')}</TableCell>
                            <TableCell style={{ backgroundColor: 'white', color: 'black' }}>{t('GameRoom:winnersDisplay.amount')}</TableCell>
                            <TableCell style={{ backgroundColor: 'white', color: 'black' }}>{t('GameRoom:winnersDisplay.pot')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody style={{ overflow: 'auto' }}>
                        {playerTurnHistory.map((turn, index) => (
                            <React.Fragment key={index}>
                                <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                                    <TableCell>
                                        <IconButton
                                            aria-label="expand row"
                                            size="small"
                                            onClick={() => toggleRow(index)}
                                            sx={{ color: 'black' }}
                                        >
                                            {expandedRows[index] ? <RemoveIcon /> : <AddIcon />}
                                        </IconButton>
                                    </TableCell>
                                    <TableCell sx={{ color: 'black' }}>{index + 1}</TableCell>
                                    <TableCell sx={{ color: 'black' }}>{turn.time}</TableCell>
                                    <TableCell sx={{ color: 'black' }}>
                                        {turn.dealerId === turn.userId && '(D) '}
                                        {turn.smallBlindId === turn.userId && '(SB) '}
                                        {turn.bigBlindId === turn.userId && '(BB) '}
                                        {turn.username}
                                    </TableCell>
                                    <TableCell sx={{ color: 'black' }}>{turn.roundName || t('GameRoom:winnersDisplay.unknown')}</TableCell>
                                    <TableCell sx={{ color: 'black' }}>{turn.selectedAction.action}</TableCell>
                                    <TableCell sx={{ color: 'black' }}>{formatCurrency(turn.selectedAction.amount)}</TableCell>
                                    <TableCell sx={{ color: 'black' }}>{formatCurrency(turn.pot)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                                        <Collapse in={expandedRows[index]} timeout="auto" unmountOnExit>
                                            {renderExtraData(turn)}
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    const renderCards = (cards: string[], winningCards: string[] = []) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: '2px' }}>
            {cards.map((card, index) => (
                <Box
                    key={index}
                    sx={{
                        border: winningCards.includes(card) ? '3px solid #00FF00' : 'none',
                        borderRadius: '4px',
                    }}
                >
                    <CardImage card={card} small />
                </Box>
            ))}
        </Box>
    );

    // Use all players from the players prop, which should include folded players
    const allPlayers = players.map(player => {
        const winnerInfo = winners.find(w => w.userId === player.userId);
        const nonWinnerInfo = nonWinners?.find(nw => nw.userId === player.userId);
        return {
            ...player,
            ...winnerInfo,
            ...nonWinnerInfo,
            isWinner: !!winnerInfo,
        };
    });
    
    // Sort players: winners first, then non-folded players, then folded players
    const sortedPlayers = allPlayers.sort((a, b) => {
        if (a.isWinner && !b.isWinner) return -1;
        if (!a.isWinner && b.isWinner) return 1;
        if (a.hasFolded && !b.hasFolded) return 1;
        if (!a.hasFolded && b.hasFolded) return -1;
        const valueA = a.handRank?.value || 0;
        const valueB = b.handRank?.value || 0;
        return valueB - valueA;
    });

    const handleOpenAnalysisModal = () => {
        setIsAnalysisModalOpen(true);
    };

    const handleCloseAnalysisModal = () => {
        setIsAnalysisModalOpen(false);
    };

    const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    return (
        <WinnerDisplay isPinned={isPinned}>
            <RoomIdDisplay>
                <Box>{t('GameRoom:winnersDisplay.roomId')} {roomId}</Box>
                {gameId && <Box>{t('GameRoom:winnersDisplay.gameId')} {gameId}</Box>}
            </RoomIdDisplay>
            <Box sx={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '10px' }}>
                <IconButton
                    onClick={handleOpenAnalysisModal}
                    sx={{ color: 'white' }}
                >
                    <AnalyticsIcon />
                </IconButton>
                <IconButton
                    onClick={onPinToggle}
                    sx={{ color: isPinned ? 'secondary.main' : 'white' }}
                >
                    <PushPinIcon />
                </IconButton>
            </Box>

            <ScrollableContent>
                <WinnersTitle>{t('GameRoom:winnersDisplay.gameResults')}</WinnersTitle>
                <WinnersTable>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('GameRoom:winnersDisplay.result')}</TableCell>
                                <TableCell>{t('GameRoom:winnersDisplay.username')}</TableCell>
                                <TableCell>{t('GameRoom:winnersDisplay.amountWon')}</TableCell>
                                <TableCell>{t('GameRoom:winnersDisplay.pot')}</TableCell>
                                <TableCell>{t('GameRoom:winnersDisplay.communityCards')}</TableCell>
                                <TableCell>{t('GameRoom:winnersDisplay.playerCards')}</TableCell>
                                <TableCell>{t('GameRoom:winnersDisplay.handRank')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedPlayers.map((player, index) => {
                                const winningCards = player.handRank?.winningCards || [];
                                return (
                                    <TableRow key={index}>
                                        <TableCell>
                                            {player.isWinner ? (
                                                <CheckCircleIcon sx={{ color: 'green', fontSize: '1.5rem' }} />
                                            ) : player.hasFolded ? (
                                                <CancelIcon sx={{ color: 'red', fontSize: '1.5rem' }} />
                                            ) : (
                                                <CancelIcon sx={{ color: 'orange', fontSize: '1.5rem' }} />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {player.username}
                                            {player.hasFolded && (
                                                <Typography component="span" sx={{ color: 'red', marginLeft: '5px' }}>
                                                    {t('GameRoom:winnersDisplay.foldedLabel')}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {player.wonAmount > 0 ? (
                                                <Typography sx={{ fontWeight: 'bold', color: '#FFD700' }}>
                                                    {formatCurrency(player.wonAmount)}
                                                </Typography>
                                            ) : (
                                                formatCurrency(player.wonAmount || 0)
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ color: player.pot > 0 ? 'lime' : 'inherit' }}>
                                                {formatCurrency(player.pot || 0)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{renderCards(communityCards, winningCards)}</TableCell>
                                        <TableCell>{renderCards(player.cards || [], winningCards)}</TableCell>
                                        <TableCell>
                                            {player.hasFolded ? t('GameRoom:winnersDisplay.folded') : (
                                                player.handRank?.handName
                                                    ? `${player.handRank.handName.split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} (${player.handRank.value}:${player.handRank.handRank})`
                                                    : t('GameRoom:winnersDisplay.na')
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </WinnersTable>

                <VerticalDivider />

                <GameplayRow>
                    <GameplayColumn>
                        <SectionTitle>{t('GameRoom:winnersDisplay.gameReplay')}</SectionTitle>
                        <GameReplayWrapper>
                            <MiniPokerGameDisplay playerTurnHistory={playerTurnHistory as any[]} />
                        </GameReplayWrapper>
                    </GameplayColumn>
                    <VerticalDivider />
                    <GameplayColumn>
                        <SectionTitle>{t('GameRoom:winnersDisplay.gameplaySummary')}</SectionTitle>
                        <GameplaySummaryContainer>
                            {renderGameplaySummary()}
                        </GameplaySummaryContainer>
                    </GameplayColumn>
                </GameplayRow>
            </ScrollableContent>

            <AnalysisModal
                open={isAnalysisModalOpen}
                onClose={handleCloseAnalysisModal}
                aria-labelledby="analysis-modal-title"
                aria-describedby="analysis-modal-description"
            >
                <AnalysisContent
                    onClose={handleCloseAnalysisModal}
                    playerTurnHistory={playerTurnHistory}
                    winners={winners}
                    nonWinners={nonWinners}
                />
            </AnalysisModal>

            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                message={copyStatus === 'success' ? t('GameRoom:winnersDisplay.analysisCopied') : t('GameRoom:winnersDisplay.analysisCopyFailed')}
            />
        </WinnerDisplay>
    );
};

export default WinnersDisplay;
